import { useTranslation } from 'react-i18next';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { useSetCryptoOnchainSandbox, type CryptoOnchainStoreStatus } from '@/lib/api/crypto-onchain';

type Props = {
  storeId: string;
  status: CryptoOnchainStoreStatus | undefined;
  /** Before the store is enabled: the mode the enable call will set. */
  sandboxOnEnable: boolean;
  onSandboxOnEnableChange: (isSandbox: boolean) => void;
  isPending: boolean;
};

/**
 * Sandbox mode of the store: test networks only (Sepolia, Solana Devnet, Tron Nile…). On an enabled
 * store the switch saves at once; before that it only sets the mode the enable call will use.
 */
export function CryptoOnchainSandboxCard({
  storeId,
  status,
  sandboxOnEnable,
  onSandboxOnEnableChange,
  isPending,
}: Props) {
  const { t } = useTranslation('integrations');
  const setSandbox = useSetCryptoOnchainSandbox();
  const isEnabled = status?.enabled === true;
  const isUnknown = isEnabled && status?.sandbox === null;
  const isChecked = isEnabled ? status?.sandbox === true : sandboxOnEnable;
  const isDisabled = isPending || setSandbox.isPending || isUnknown;
  const hint = isUnknown
    ? t('cryptoOnchain.sandbox.unknown')
    : isChecked
      ? t('cryptoOnchain.sandbox.hintOn')
      : t('cryptoOnchain.sandbox.hintOff');

  const handleCheckedChange = (isSandbox: boolean) => {
    if (!isEnabled) {
      onSandboxOnEnableChange(isSandbox);
      return;
    }
    setSandbox.mutate({ storeId, payoutAddresses: {}, sandbox: isSandbox });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{t('cryptoOnchain.sandbox.title')}</CardTitle>
        <CardDescription>{t('cryptoOnchain.sandbox.description')}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-2">
        <div className="flex items-center gap-3">
          <Switch
            id="crypto-onchain-sandbox"
            checked={isChecked}
            disabled={isDisabled}
            onCheckedChange={handleCheckedChange}
          />
          <Label htmlFor="crypto-onchain-sandbox">{t('cryptoOnchain.sandbox.label')}</Label>
        </div>
        <p className="text-muted-foreground text-xs">{hint}</p>
      </CardContent>
    </Card>
  );
}
